import React, { useMemo, useState, useEffect, useRef } from "react";
import { SearchForm } from "../../organisms/form/SearchForm";
import { useHome } from "./hook";
import styled from "styled-components";
import InquiryForm from "../../organisms/form/InquiryForm";
import { PropertyCard } from "../../molecules/PropertyCard";
import { buildImageUrl } from "../../../utils/imageUrl";
import { SEO } from "../../atoms/SEO";
import { useLanguage } from "../../../contexts/LanguageContext";
export const HomePage = () => {
  const { handleSearch, hotProperties, homepageImage } = useHome();
  const { t } = useLanguage();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const seoTitle = "LIVINGPP | Your Premier Real Estate Partner in Cambodia";
  const seoDescription = "Find your dream home with LIVINGPP. We offer the best residential and commercial properties for sale and rent in Phnom Penh and across Cambodia.";

  // Memoize the background image URL to prevent recalculation on every render
  const backgroundImageUrl = useMemo(() => {
    return homepageImage?.image ? buildImageUrl(homepageImage.image) : "";
  }, [homepageImage?.image]);

  // Preload the image and track loading state
  useEffect(() => {
    if (!backgroundImageUrl) {
      setIsImageLoaded(false);
      return;
    }

    // Reset loading state when image URL changes
    setIsImageLoaded(false);

    // Create a new image element to preload
    const img = new Image();
    imgRef.current = img;

    img.onload = () => {
      // Only set loaded state if this is still the current image
      if (imgRef.current === img) {
        setIsImageLoaded(true);
      }
    };

    img.onerror = () => {
      // If image fails to load, still set loaded to true to show fallback
      if (imgRef.current === img) {
        setIsImageLoaded(true);
      }
    };

    img.src = backgroundImageUrl;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
      if (imgRef.current === img) {
        imgRef.current = null;
      }
    };
  }, [backgroundImageUrl]);

  const mappedProperties = hotProperties.slice(0, 6).map((property: any) => ({
    id: property.id,
    property_name: property.property_name,
    price: property.price,
    size: property.size,
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    description: property.description,
    thumbnail_image: property.thumbnail_image,
    property_status: property.property_status,
    property_status_display: property.property_status_display,
    property_type: property.property_type,
    district: property.district,
    commune: property.commune,
    is_hot: property.is_hot,
  }));

  return (
    <Container>
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={window.location.origin}
      />
      <SearchContainer $backgroundImage={isImageLoaded ? backgroundImageUrl : undefined}>
        <SearchForm onSubmit={handleSearch} />
      </SearchContainer>

      <Section>
        <SectionTitle>{t('property.hotProperties')}</SectionTitle>
        <CardGrid>
          {mappedProperties.map((property) => (
            <PropertyCard key={property.id} property={property} type="popular-property" />
          ))}
        </CardGrid>
      </Section>
      <FormSection>
        <InquiryForm />
      </FormSection>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #f8fafc 0%, #e9f0fa 100%);
  width: 100%;
`;

const SearchContainer = styled.div<{ $backgroundImage?: string }>`
  width: 100%;
  margin-top: 0;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: ${({ $backgroundImage }) =>
    $backgroundImage
      ? `url(${$backgroundImage}) center center / cover no-repeat`
      : "linear-gradient(180deg, #f8fafc 0%, #e9f0fa 100%)"};
  z-index: 1;

  transition: opacity 0.3s ease-in-out;
  
  @media (min-width: 768px) {
    min-height: 70vh;
  }
  
  @media (min-width: 1024px) {
    min-height: 80vh;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(255,255,255,0.1); /* Optional: overlay for readability */
    z-index: 2;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 3;
  }
`;

const Section = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 24px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    margin: 32px auto;
  }
  
  @media (min-width: 1024px) {
    margin: 48px auto;
  }
`;

const FormSection = styled.div`
  width: 100%;
  background: #e9ecef;
  align-items: center;
  display: flex;
  padding: 0;
  margin: 0;
  
  @media (min-width: 768px) {
    padding: 16px 0;
  }
  
  @media (min-width: 1024px) {
    padding: 32px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #222;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 28px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 1024px) {
    font-size: 32px;
    margin-bottom: 30px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  justify-items: center;
  width: calc(100% - 24px);
  margin: 0 12px;
  
  @media (min-width: 400px) {
    gap: 24px;
    width: calc(100% - 32px);
    margin: 0 16px;
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
    width: calc(100% - 48px);
    margin: 0 24px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
    width: calc(100% - 64px);
    margin: 0 32px;
  }
`;
